/* eslint-disable no-case-declarations */
import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import styled from 'styled-components';
import moment from 'moment';
import { fixerPhotoUrl } from '../../../utils/fixers';

// Components
import Text from '../../shared/atoms/Text';
import Badge from '../../shared/atoms/Badge';
import Row from '../../shared/atoms/Row';
import Box from '../../shared/atoms/Box';
import BadgeIcon from '../../shared/atoms/BadgeIcon';
import Button from '../../shared/atoms/Button';
import Separator from '../../Separator';
import Search16 from '../../icons/Search16';
import CalendarCheck16 from '../../icons/CalendarCheck16';
import Time16 from '../../icons/Time16';
import Check16 from '../../icons/Check16';
import NotificationDot16 from '../../icons/NotificationDot16';
import CalendarProposalColor32 from '../../icons/CalendarProposalColor32';

const FixerPhoto = styled('img')`
  width: 24px;
  border-radius: 100%;
  margin-right: 4px;
`;

const ConfirmationBanner = styled(Box)`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 8px 8px 0 0;
  padding: 16px 20px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 16px;

  @media (max-width: ${(p) => p.theme.breakpoints.md}) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const ConfirmationText = styled(Text)`
  color: white;
  font-weight: 600;
  font-size: 16px;
  line-height: 1.4;
`;

const CounterProposalBanner = styled(Box)`
  background: linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%);
  border: 2px solid #d5ff4f;
  border-radius: 8px;
  padding: 12px 16px;
  margin-top: 12px;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: linear-gradient(135deg, #dcedc8 0%, #e8f5e9 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(213, 255, 79, 0.3);
  }
`;

const CounterProposalContent = styled(Box)`
  flex: 1;
`;

const CounterProposalTitle = styled(Text)`
  font-weight: 600;
  color: #2e414b;
  font-size: 14px;
  margin-bottom: 2px;
`;

const CounterProposalSubtitle = styled(Text)`
  color: #5a6a72;
  font-size: 12px;
`;

const CounterProposalArrow = styled.span`
  color: #2e414b;
  font-size: 18px;
  font-weight: bold;
`;

const VisitReportBanner = styled(Box)`
  background: linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%);
  border: 2px solid #7c3aed;
  border-radius: 8px;
  padding: 12px 16px;
  margin-top: 12px;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: linear-gradient(135deg, #ddd6fe 0%, #c4b5fd 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(124, 58, 237, 0.25);
  }
`;

const CourseCard = ({ course, channels }) => {
  const { t, i18n } = useTranslation();
  const channel = channels.find((e) => e.id.endsWith(`_${course.fixer_id}`));
  return (
    <Box mb={5}>
      {course.confirmation_url && (
        <ConfirmationBanner>
          <ConfirmationText>⏰ {t('course.confirm.banner')}</ConfirmationText>
          <Button
            variant="filledWhite"
            href={course.confirmation_url}
            target="_blank"
            style={{ whiteSpace: 'nowrap' }}>
            {t('course.confirm.cta')}
          </Button>
        </ConfirmationBanner>
      )}
      <Box
        borderColor="gray.light"
        borderWidth="1px"
        borderRadius={course.confirmation_url ? '0 0 8px 8px' : 'sm'}
        borderStyle="solid"
        key={course.id}
        p={{ xs: 2, lg: 5 }}>
        <Box
          flexDirection="row"
          justifyContent="space-between"
          pt={{ xs: 2, lg: 0 }}
          px={{ xs: 2, lg: 0 }}>
          <Box flexDirection={{ xs: 'column', lg: 'row' }}>
            <Text mr={1} color="black">
              {t('request')} <b>#{course.id}</b>
            </Text>
          </Box>
          {channel && channel.unreadCount > 0 && course.state == 'scheduled' && (
            <Badge before={<NotificationDot16 />} light>
              {t('coursecard.newmessage')}
            </Badge>
          )}
          {course.state === 'finished' && (
            <Box flexDirection={{ xs: 'column', lg: 'row' }}>
              <Text mr={{ xs: 0, lg: 1 }} fontWeight={500}>
                {t('coursecard.total')}
              </Text>
              <Text textAlign="right" fontWeight={2}>
                {course.prix}
                {course.currency}
              </Text>
            </Box>
          )}
        </Box>
        <Separator mt={4} axe="horizontal" />
        <Row md gap={16}>
          <Box style={{ flex: 3 }} pt={{ xs: 2, lg: 0 }} px={{ xs: 2, lg: 0 }}>
            {course.state === 'dispatched' && (
              <Box flexDirection="row">
                <BadgeIcon
                  icon={<Search16 color="white" />}
                  color="blue.dark"
                  mr={2}
                />
                <Text fontWeight={2} mb={2} variant="large" color="black">
                  {t('coursecard.lookingforfixer')}
                </Text>
              </Box>
            )}
            {course.state === 'scheduled' && (
              <Box flexDirection="row">
                <BadgeIcon
                  icon={<CalendarCheck16 color="white" />}
                  color="blue.dark"
                  mr={2}
                />
                <Text fontWeight={2} mb={2} variant="large" color="black">
                  {moment(course.date).format('LLL')}
                </Text>
              </Box>
            )}
            {course.state === 'finished' && (
              <Box flexDirection="row">
                <BadgeIcon
                  icon={<Check16 color="white" />}
                  color="green.dark"
                  mr={2}
                />
                <Text fontWeight={2} mb={2} variant="large" color="black">
                  {moment(course.date).format('LLL')}
                </Text>
              </Box>
            )}
            {course.state === 'cancelled' && (
              <Box flexDirection="row">
                <BadgeIcon
                  icon={<Time16 color="white" />}
                  color="primary.darker"
                  mr={2}
                />
                <Text
                  fontWeight={2}
                  mb={2}
                  color="primary.darker"
                  variant="large">
                  {t('coursecard.finished.text')}
                </Text>
              </Box>
            )}
            <Text>
              <b style={{ fontWeight: 500 }}>{t('address')}</b> :{' '}
              {course.position}
            </Text>
            {course.fixer_id && (
              <Text mt={2}>
                <b style={{ fontWeight: 500 }}>{t('fixer')}</b> :{' '}
                <FixerPhoto src={fixerPhotoUrl(course.fixer)} />
                {course.fixer.prenom}
              </Text>
            )}
            {/* Visit report banner for finished courses with published report.
                Wording adapts to the API payload:
                - total_ttc_estimated > 0 : action-oriented "Planifier un RDV"
                  + "Un devis de N€ TTC" (DEVIS-14b)
                - otherwise (null = API not deployed yet, or 0 = empty rapport) :
                  fall back to the V1.0 generic "Rapport de visite disponible"
                  wording so the banner stays useful even without the new field. */}
            {course.state === 'finished' &&
              course.visit_report &&
              course.visit_report.published_at && (
                <VisitReportBanner
                  as="a"
                  href={`/courses/${course.id}`}
                  data-testid="visit-report-banner">
                  <CalendarProposalColor32 />
                  <CounterProposalContent>
                    <CounterProposalTitle>
                      {course.visit_report.total_ttc_estimated > 0
                        ? t('coursecard.visitReport.title.withTotal')
                        : t('coursecard.visitReport.title')}
                    </CounterProposalTitle>
                    <CounterProposalSubtitle>
                      {course.visit_report.total_ttc_estimated > 0
                        ? t('coursecard.visitReport.subtitle.withTotal', {
                            total: new Intl.NumberFormat(
                              i18n.language === 'en' ? 'en-GB' : 'fr-FR',
                              {
                                style: 'currency',
                                currency: 'EUR',
                                maximumFractionDigits: 0,
                              },
                            ).format(course.visit_report.total_ttc_estimated),
                            fixerName: course.fixer && course.fixer.prenom,
                          })
                        : t('coursecard.visitReport.subtitle', {
                            fixerName: course.fixer && course.fixer.prenom,
                          })}
                    </CounterProposalSubtitle>
                  </CounterProposalContent>
                  <CounterProposalArrow>›</CounterProposalArrow>
                </VisitReportBanner>
              )}
            {/* Counter-proposal banner for dispatched courses */}
            {course.state === 'dispatched' && course.has_counter_proposals && (
              <CounterProposalBanner as="a" href={`/courses/${course.id}`}>
                <CalendarProposalColor32 />
                <CounterProposalContent>
                  <CounterProposalTitle>
                    {t('counterProposals.bannerTitle')}
                  </CounterProposalTitle>
                  <CounterProposalSubtitle>
                    {t('counterProposals.bannerSubtitle')}
                  </CounterProposalSubtitle>
                </CounterProposalContent>
                <CounterProposalArrow>›</CounterProposalArrow>
              </CounterProposalBanner>
            )}
          </Box>
          <Box>
            <Button fullWidth variant="filled" to={`/courses/${course.id}`}>
              {t('seedetails')}
            </Button>
          </Box>
        </Row>
      </Box>
    </Box>
  );
};

export default CourseCard;
